import {
  Delete as IconDelete,
  GetApp as IconGetApp,
  Person as IconPerson,
  BubbleChart as IconBubbleChart
} from '@mui/icons-material';
import ActionOnSelectedItems, {
  ActionConfig
} from 'Components/Shared/ActionOnSelectedItems/ActionOnSelectedItems';
import useTranslate from 'Hooks/useTranslate';
import TestIds from 'Tests/TestIds';
import AddSubscriptions from './AddSubscriptions';
import ExportUsers from './ExportUsers';
import RemoveGuestUsers from './RemoveGuestUsers';
import RemoveUsers from './RemoveUsers';
import { UsersScope } from '../UsersScope';

interface Props {
  selectedIds: string[];
  selectAllEnabled: boolean;
  selectedCount: number;
  totalCount: number;
  onReloadUsers: () => void;
  disabled: boolean;
  searchTerm: string;
  onSelect: (ids: string[] | null) => void;
  loading: boolean;
  canDeleteUsers: boolean;
  canSubscribeUsers: boolean;
  scope: UsersScope;
}

function ActionOnSelectedUsers(props: Readonly<Props>) {
  const ts = useTranslate();
  const {
    selectedCount,
    totalCount,
    selectedIds,
    selectAllEnabled,
    onReloadUsers,
    onSelect,
    disabled,
    searchTerm,
    loading,
    canDeleteUsers,
    canSubscribeUsers,
    scope
  } = props;

  const actions: ActionConfig[] = [
    ...(canSubscribeUsers
      ? [
        {
          key: 'addSubscriptions',
          icon: <IconBubbleChart />,
          tooltip: ts('common.action.subscribe'),
          testId: TestIds.pages.users.subscriptionButton,
          disabled,
          renderMode: 'accordion' as const,
          renderAccordionContent: (onCollapse: () => void) => (
            <AddSubscriptions
              selectedUserIds={selectedIds}
              selectAllUserEnabled={selectAllEnabled}
              selectedUserCount={selectedCount}
              userFilter={searchTerm}
              onCollapse={onCollapse}
            />
          )
        }
      ]
      : []),
    ...(canDeleteUsers && scope.kind === 'organizationGuests'
      ? [
        {
          key: 'removeGuestUsers',
          icon: <IconDelete />,
          tooltip: ts('common.action.delete'),
          testId:
            TestIds.pages.users.actionOnSelectedUsers.removeGuestUsersButton,
          disabled,
          renderMode: 'popper' as const,
          renderPopperContent: (onCollapse: () => void) => (
            <RemoveGuestUsers
              selectedUserIds={selectedIds}
              selectAllUserEnabled={selectAllEnabled}
              selectedUserCount={selectedCount}
              userFilter={searchTerm}
              organizationCode={scope.organizationCode}
              onCollapse={onCollapse}
              onReloadUsers={onReloadUsers}
            />
          )
        }
      ]
      : []),
    ...(canDeleteUsers && scope.kind !== 'organizationGuests'
      ? [
        {
          key: 'removeUsers',
          icon: <IconDelete />,
          tooltip: ts('common.action.delete'),
          testId:
            TestIds.pages.users.actionOnSelectedUsers.removeUsersButton,
          disabled,
          renderMode: 'popper' as const,
          renderPopperContent: (onCollapse: () => void) => (
            <RemoveUsers
              selectedUserIds={selectedIds}
              selectAllUserEnabled={selectAllEnabled}
              selectedUserCount={selectedCount}
              userFilter={searchTerm}
              onCollapse={onCollapse}
              onReloadUsers={onReloadUsers}
            />
          )
        }
      ]
      : []),
    {
      key: 'exportUsers',
      icon: <IconGetApp />,
      tooltip: ts('common.action.export'),
      disabled,
      renderMode: 'accordion' as const,
      renderAccordionContent: (onCollapse: () => void) => (
        <ExportUsers
          selectedUserIds={selectedIds}
          searchTerm={searchTerm}
          selectAllUserEnabled={selectAllEnabled}
          onCollapse={onCollapse}
          scope={scope}
        />
      )
    }
  ];

  return (
    <ActionOnSelectedItems
      selectedIds={selectedIds}
      selectAllEnabled={selectAllEnabled}
      selectedCount={selectedCount}
      totalCount={totalCount}
      onSelect={onSelect}
      disabled={disabled}
      loading={loading}
      searchTerm={searchTerm}
      icon={<IconPerson color="primary" />}
      selectionLabel={ts('page.users.index.selectedUserCount', {
        count: selectedCount,
        totalCount: totalCount
      })}
      allSelectedLabel={ts('page.users.index.allUsersSelected', {
        totalCount: totalCount
      })}
      actions={actions}
    />
  );
}

export default ActionOnSelectedUsers;
